import { useSearchParams } from 'react-router-dom';
import { PageHead } from '@/components/ui';
import { Icon } from '@/components/common/NavIcons';
import { haptic } from '@/lib/haptics'; // HAPTIC_FEEDBACK
import { UnitsPage } from './Units';
import { DoctorsPage } from './Doctors';

type ClientTab = 'consultorios' | 'doctores';

/**
 * Consultorios y doctores son las dos caras del mismo cliente, así que viven
 * en una sola pantalla con pestañas en vez de dos destinos separados en el
 * menú. El tab se guarda en la URL (`?tab=doctores`) para que un enlace a
 * «Doctores» — el dashboard, un marcador viejo — pueda seguir aterrizando
 * directo ahí en vez de siempre abrir en Consultorios.
 */
export function ClientsPage() {
  const [params, setParams] = useSearchParams();
  const tab: ClientTab = params.get('tab') === 'doctores' ? 'doctores' : 'consultorios';

  function setTab(next: ClientTab) {
    if (next === tab) return;
    haptic.light(); // HAPTIC_FEEDBACK
    setParams(next === 'consultorios' ? {} : { tab: next }, { replace: true });
  }

  return (
    <>
      <PageHead
        title="Clientes"
        subtitle="Los consultorios que se atienden y los médicos asociados a ellos."
      />

      {/* El ícono al lado del nombre: las dos pestañas dicen "clientes" pero
          son entidades distintas —un espacio y una persona—, y a la hora de
          volver a la pantalla se reconoce antes por la figura que leyendo.

          `aria-pressed` es la deuda de §2.3 de la auditoría de UI/UX: cuál
          está activa se comunicaba solo con una clase de CSS, o sea con nada
          para quien no ve la pantalla. Se usa `aria-pressed` y no
          `role="tab"` porque un `tablist` de verdad obliga a mover el foco con
          las flechas y a que el panel sea un `tabpanel` enlazado; acá son dos
          botones que alternan contenido, y decir eso es más honesto que
          declarar un patrón que no se implementa entero. */}
      <div className="tabs mb-3">
        <button
          type="button"
          aria-pressed={tab === 'consultorios'}
          className={`tab${tab === 'consultorios' ? ' is-active' : ''}`}
          onClick={() => setTab('consultorios')}
        >
          <Icon name="consultorio" size={15} />
          Consultorios
        </button>
        <button
          type="button"
          aria-pressed={tab === 'doctores'}
          className={`tab${tab === 'doctores' ? ' is-active' : ''}`}
          onClick={() => setTab('doctores')}
        >
          <Icon name="doctor" size={15} />
          Doctores
        </button>
      </div>

      {tab === 'consultorios' ? <UnitsPage embedded /> : <DoctorsPage embedded />}
    </>
  );
}
